import { useEffect, useMemo, useRef, useState } from 'react';
import PageBanner from '../components/ui/PageBanner';
import {
  JOBS,
  DEPARTMENTS,
  JOB_LOCATIONS,
  PAKISTAN_PROVINCES,
  getJobById,
} from '../data/careers';
import {
  countWords,
  evaluateEligibility,
  isFormComplete,
} from '../utils/jobEligibility';
import './Branches.css';

const MAX_ENTRIES = 4;
const MIN_STATEMENT_WORDS = 100;
const MAX_STATEMENT_WORDS = 200;
const MAX_FILE_SIZE_MB = 5;

const GENDERS = ['Male', 'Female', 'Other', 'Prefer not to say'];
const MARITAL_STATUSES = ['Single', 'Married', 'Divorced', 'Widowed'];
const NATIONALITIES = ['Pakistani', 'Afghan', 'Other'];

function emptyEducation() {
  return {
    degree: '',
    fieldOfStudy: '',
    institute: '',
    boardUniversity: '',
    passingYear: '',
    cgpaDivision: '',
  };
}

function emptyExperience() {
  return {
    organization: '',
    designation: '',
    startDate: '',
    endDate: '',
    currentlyWorking: false,
    responsibilities: '',
    reasonForLeaving: '',
  };
}

function FormSection({ title, children, disabled }) {
  return (
    <fieldset className={`career__form-section ${disabled ? 'career__form-section--disabled' : ''}`} disabled={disabled}>
      <legend className="career__form-section-title">{title}</legend>
      {children}
    </fieldset>
  );
}

function FieldLabel({ htmlFor, children, required }) {
  return (
    <label className="career__field-label" htmlFor={htmlFor}>
      {children}
      {required ? <span className="career__required"> *</span> : null}
    </label>
  );
}

export default function CareerApply({ setPage, route }) {
  const jobId = route?.params?.jobId;
  const initial = useMemo(() => getJobById(jobId).id, [jobId]);
  const [applyJobId, setApplyJobId] = useState(initial);

  const selectedJob = useMemo(() => getJobById(applyJobId), [applyJobId]);

  const [personal, setPersonal] = useState({
    fullName: '',
    fatherName: '',
    cnic: '',
    dateOfBirth: '',
    gender: '',
    maritalStatus: '',
    nationality: '',
  });

  const [contact, setContact] = useState({
    mobile: '',
    alternateMobile: '',
    email: '',
    currentAddress: '',
    permanentAddress: '',
    city: '',
    province: '',
  });

  const [educations, setEducations] = useState([emptyEducation()]);
  const [experiences, setExperiences] = useState([emptyExperience()]);

  const [positionPrefs, setPositionPrefs] = useState({
    department: selectedJob.department,
    preferredLocation: '',
  });

  const [personalStatement, setPersonalStatement] = useState('');

  const [confirmations, setConfirmations] = useState({
    accurateInfo: false,
    meetsRequirements: false,
    agreesToPolicies: false,
  });

  const [cvFile, setCvFile] = useState(null);
  const [supportingFiles, setSupportingFiles] = useState([]);

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [eligibilityVisible, setEligibilityVisible] = useState(false);
  const eligibilityShownRef = useRef(false);

  const statementWordCount = countWords(personalStatement);

  const formComplete = useMemo(
    () =>
      isFormComplete({
        personal,
        contact,
        educations,
        experiences,
        personalStatement,
        confirmations,
        cvFile,
        minStatementWords: MIN_STATEMENT_WORDS,
        maxStatementWords: MAX_STATEMENT_WORDS,
      }),
    [personal, contact, educations, experiences, personalStatement, confirmations, cvFile]
  );

  const screeningFormData = useMemo(
    () => ({
      dateOfBirth: personal.dateOfBirth,
      educations,
      experiences,
    }),
    [personal.dateOfBirth, educations, experiences]
  );

  const eligibilityResult = useMemo(() => {
    if (!formComplete) return null;
    return evaluateEligibility(selectedJob, screeningFormData);
  }, [formComplete, selectedJob, screeningFormData]);

  const isEligible = Boolean(eligibilityResult?.eligible);

  useEffect(() => {
    if (!formComplete) {
      setEligibilityVisible(false);
      eligibilityShownRef.current = false;
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setEligibilityVisible(true);

      if (!eligibilityShownRef.current) {
        eligibilityShownRef.current = true;
        document.getElementById('eligibility-screening')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [formComplete]);

  const updatePersonal = (field, value) => {
    setPersonal((prev) => ({ ...prev, [field]: value }));
  };

  const updateContact = (field, value) => setContact((prev) => ({ ...prev, [field]: value }));

  const updatePositionPrefs = (field, value) => {
    setPositionPrefs((prev) => ({ ...prev, [field]: value }));
  };

  const updateEducation = (index, field, value) => {
    setEducations((prev) => prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)));
  };

  const addEducation = () => {
    if (educations.length < MAX_ENTRIES) setEducations((prev) => [...prev, emptyEducation()]);
  };

  const removeEducation = (index) => {
    if (educations.length > 1) {
      setEducations((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const updateExperience = (index, field, value) => {
    setExperiences((prev) => prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)));
  };

  const addExperience = () => {
    if (experiences.length < MAX_ENTRIES) setExperiences((prev) => [...prev, emptyExperience()]);
  };

  const removeExperience = (index) => {
    if (experiences.length > 1) {
      setExperiences((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const validateFile = (file, label) => {
    if (!file) return `${label} is required.`;
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `${label} must be under ${MAX_FILE_SIZE_MB} MB.`;
    }
    return null;
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!personal.fullName.trim()) nextErrors.fullName = 'Full name is required.';
    if (!personal.fatherName.trim()) nextErrors.fatherName = "Father's name is required.";
    if (!personal.cnic.trim()) nextErrors.cnic = 'CNIC number is required.';
    if (!personal.dateOfBirth) nextErrors.dateOfBirth = 'Date of birth is required.';
    if (!personal.gender) nextErrors.gender = 'Gender is required.';
    if (!personal.maritalStatus) nextErrors.maritalStatus = 'Marital status is required.';
    if (!personal.nationality) nextErrors.nationality = 'Nationality is required.';

    if (!contact.mobile.trim()) nextErrors.mobile = 'Mobile number is required.';
    if (!contact.email.trim()) nextErrors.email = 'Email address is required.';
    if (!contact.currentAddress.trim()) nextErrors.currentAddress = 'Current address is required.';
    if (!contact.permanentAddress.trim()) nextErrors.permanentAddress = 'Permanent address is required.';
    if (!contact.city.trim()) nextErrors.city = 'City is required.';
    if (!contact.province) nextErrors.province = 'Province is required.';

    educations.forEach((edu, i) => {
      if (!edu.degree.trim()) nextErrors[`edu-degree-${i}`] = 'Degree is required.';
      if (!edu.fieldOfStudy.trim()) nextErrors[`edu-field-${i}`] = 'Field of study is required.';
      if (!edu.institute.trim()) nextErrors[`edu-institute-${i}`] = 'Institute / university is required.';
      if (!edu.boardUniversity.trim()) nextErrors[`edu-board-${i}`] = 'Board / university is required.';
      if (!edu.passingYear.trim()) nextErrors[`edu-year-${i}`] = 'Passing year is required.';
    });

    experiences.forEach((exp, i) => {
      if (!exp.organization.trim()) nextErrors[`exp-org-${i}`] = 'Organization is required.';
      if (!exp.designation.trim()) nextErrors[`exp-designation-${i}`] = 'Designation is required.';
      if (!exp.startDate) nextErrors[`exp-start-${i}`] = 'Employment start date is required.';
      if (!exp.currentlyWorking && !exp.endDate) nextErrors[`exp-end-${i}`] = 'Employment end date is required.';
      if (!exp.responsibilities.trim()) nextErrors[`exp-resp-${i}`] = 'Key responsibilities are required.';
    });

    if (statementWordCount < MIN_STATEMENT_WORDS) {
      nextErrors.personalStatement = `Personal statement must be at least ${MIN_STATEMENT_WORDS} words.`;
    } else if (statementWordCount > MAX_STATEMENT_WORDS) {
      nextErrors.personalStatement = `Personal statement must not exceed ${MAX_STATEMENT_WORDS} words.`;
    }

    if (!confirmations.accurateInfo) nextErrors.accurateInfo = 'Please confirm your information is accurate.';
    if (!confirmations.meetsRequirements) nextErrors.meetsRequirements = 'Please confirm you meet the eligibility criteria.';
    if (!confirmations.agreesToPolicies) nextErrors.agreesToPolicies = 'Please agree to the recruitment policies.';

    const cvError = validateFile(cvFile, 'CV / Resume');
    if (cvError) nextErrors.cvFile = cvError;

    if (!isEligible) {
      nextErrors.eligibility = 'You must pass eligibility screening before submitting your application.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleJobChange = (newJobId) => {
    setApplyJobId(newJobId);
    const job = getJobById(newJobId);
    setPositionPrefs({
      department: job.department,
      preferredLocation: '',
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (submitted && isEligible) {
    return (
      <>
        <PageBanner title="Application Submitted" crumbs={['Home', 'Careers', selectedJob.title, 'Apply']} />
        <section className="section section--gray">
          <div className="section-inner">
            <div className="career__apply card">
              <div className="career__eligibility career__eligibility--eligible">
                <h3>✅ Eligible for this Job</h3>
                <p>
                  Thank you, {personal.fullName}. Your application for <strong>{selectedJob.title}</strong> has been
                  received.
                </p>
                <p className="section-text">
                  Our HR team will review your application and contact you if you are shortlisted for an interview.
                </p>
              </div>
              <div className="career__actions">
                <button type="button" className="btn-outline" onClick={() => setPage('branches')}>
                  Back to Careers
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  style={{ marginLeft: 10 }}
                  onClick={() => setPage('career-details', { params: { jobId: applyJobId } })}
                >
                  View Job Details
                </button>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <PageBanner title="Apply Now" crumbs={['Home', 'Careers', selectedJob.title, 'Apply']} />

      <section className="section section--gray">
        <div className="section-inner">
          <div className="career__apply card">
            <div className="career__apply-head">
              <p className="section-label">How to Apply</p>
              <h3 className="section-title">Apply for {selectedJob.title}</h3>
              <p className="section-text">
                Complete all sections below and upload your CV. Eligibility will be checked once the form is fully filled.
              </p>
            </div>

            <form className="career__form" onSubmit={handleSubmit} noValidate>
              <FormSection title="1. Personal Information">
                <div className="career__form-grid">
                  <div className="career__field">
                    <FieldLabel htmlFor="fullName" required>Full Name</FieldLabel>
                    <input
                      id="fullName"
                      className={`input ${errors.fullName ? 'input--error' : ''}`}
                      value={personal.fullName}
                      onChange={(e) => updatePersonal('fullName', e.target.value)}
                    />
                    {errors.fullName ? <span className="career__field-error">{errors.fullName}</span> : null}
                  </div>

                  <div className="career__field">
                    <FieldLabel htmlFor="fatherName" required>Father&apos;s Name</FieldLabel>
                    <input
                      id="fatherName"
                      className={`input ${errors.fatherName ? 'input--error' : ''}`}
                      value={personal.fatherName}
                      onChange={(e) => updatePersonal('fatherName', e.target.value)}
                    />
                    {errors.fatherName ? <span className="career__field-error">{errors.fatherName}</span> : null}
                  </div>

                  <div className="career__field">
                    <FieldLabel htmlFor="cnic" required>CNIC Number</FieldLabel>
                    <input
                      id="cnic"
                      className={`input ${errors.cnic ? 'input--error' : ''}`}
                      placeholder="XXXXX-XXXXXXX-X"
                      value={personal.cnic}
                      onChange={(e) => updatePersonal('cnic', e.target.value)}
                    />
                    {errors.cnic ? <span className="career__field-error">{errors.cnic}</span> : null}
                  </div>

                  <div className="career__field">
                    <FieldLabel htmlFor="dateOfBirth" required>Date of Birth</FieldLabel>
                    <input
                      id="dateOfBirth"
                      type="date"
                      className={`input ${errors.dateOfBirth ? 'input--error' : ''}`}
                      value={personal.dateOfBirth}
                      onChange={(e) => updatePersonal('dateOfBirth', e.target.value)}
                    />
                    {errors.dateOfBirth ? <span className="career__field-error">{errors.dateOfBirth}</span> : null}
                  </div>

                  <div className="career__field">
                    <FieldLabel htmlFor="gender" required>Gender</FieldLabel>
                    <select
                      id="gender"
                      className={`input ${errors.gender ? 'input--error' : ''}`}
                      value={personal.gender}
                      onChange={(e) => updatePersonal('gender', e.target.value)}
                    >
                      <option value="">Select gender</option>
                      {GENDERS.map((g) => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                    {errors.gender ? <span className="career__field-error">{errors.gender}</span> : null}
                  </div>

                  <div className="career__field">
                    <FieldLabel htmlFor="maritalStatus" required>Marital Status</FieldLabel>
                    <select
                      id="maritalStatus"
                      className={`input ${errors.maritalStatus ? 'input--error' : ''}`}
                      value={personal.maritalStatus}
                      onChange={(e) => updatePersonal('maritalStatus', e.target.value)}
                    >
                      <option value="">Select status</option>
                      {MARITAL_STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    {errors.maritalStatus ? <span className="career__field-error">{errors.maritalStatus}</span> : null}
                  </div>

                  <div className="career__field">
                    <FieldLabel htmlFor="nationality" required>Nationality</FieldLabel>
                    <select
                      id="nationality"
                      className={`input ${errors.nationality ? 'input--error' : ''}`}
                      value={personal.nationality}
                      onChange={(e) => updatePersonal('nationality', e.target.value)}
                    >
                      <option value="">Select nationality</option>
                      {NATIONALITIES.map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                    {errors.nationality ? <span className="career__field-error">{errors.nationality}</span> : null}
                  </div>
                </div>
              </FormSection>

              <FormSection title="2. Contact Details">
                <div className="career__form-grid">
                  <div className="career__field">
                    <FieldLabel htmlFor="mobile" required>Mobile Number</FieldLabel>
                    <input
                      id="mobile"
                      className={`input ${errors.mobile ? 'input--error' : ''}`}
                      placeholder="+92 3XX XXXXXXX"
                      value={contact.mobile}
                      onChange={(e) => updateContact('mobile', e.target.value)}
                    />
                    {errors.mobile ? <span className="career__field-error">{errors.mobile}</span> : null}
                  </div>

                  <div className="career__field">
                    <FieldLabel htmlFor="alternateMobile">Alternate Mobile Number</FieldLabel>
                    <input
                      id="alternateMobile"
                      className="input"
                      placeholder="+92 3XX XXXXXXX"
                      value={contact.alternateMobile}
                      onChange={(e) => updateContact('alternateMobile', e.target.value)}
                    />
                  </div>

                  <div className="career__field">
                    <FieldLabel htmlFor="email" required>Email Address</FieldLabel>
                    <input
                      id="email"
                      type="email"
                      className={`input ${errors.email ? 'input--error' : ''}`}
                      value={contact.email}
                      onChange={(e) => updateContact('email', e.target.value)}
                    />
                    {errors.email ? <span className="career__field-error">{errors.email}</span> : null}
                  </div>

                  <div className="career__field career__field--full">
                    <FieldLabel htmlFor="currentAddress" required>Current Address</FieldLabel>
                    <input
                      id="currentAddress"
                      className={`input ${errors.currentAddress ? 'input--error' : ''}`}
                      value={contact.currentAddress}
                      onChange={(e) => updateContact('currentAddress', e.target.value)}
                    />
                    {errors.currentAddress ? <span className="career__field-error">{errors.currentAddress}</span> : null}
                  </div>

                  <div className="career__field career__field--full">
                    <FieldLabel htmlFor="permanentAddress" required>Permanent Address</FieldLabel>
                    <input
                      id="permanentAddress"
                      className={`input ${errors.permanentAddress ? 'input--error' : ''}`}
                      value={contact.permanentAddress}
                      onChange={(e) => updateContact('permanentAddress', e.target.value)}
                    />
                    {errors.permanentAddress ? (
                      <span className="career__field-error">{errors.permanentAddress}</span>
                    ) : null}
                  </div>

                  <div className="career__field">
                    <FieldLabel htmlFor="city" required>City</FieldLabel>
                    <input
                      id="city"
                      className={`input ${errors.city ? 'input--error' : ''}`}
                      value={contact.city}
                      onChange={(e) => updateContact('city', e.target.value)}
                    />
                    {errors.city ? <span className="career__field-error">{errors.city}</span> : null}
                  </div>

                  <div className="career__field">
                    <FieldLabel htmlFor="province" required>Province</FieldLabel>
                    <select
                      id="province"
                      className={`input ${errors.province ? 'input--error' : ''}`}
                      value={contact.province}
                      onChange={(e) => updateContact('province', e.target.value)}
                    >
                      <option value="">Select province</option>
                      {PAKISTAN_PROVINCES.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                    {errors.province ? <span className="career__field-error">{errors.province}</span> : null}
                  </div>
                </div>
              </FormSection>

              <FormSection title="3. Educational Qualifications">
                {educations.map((edu, index) => (
                  <div key={index} className="career__repeat-block">
                    <div className="career__repeat-head">
                      <strong>Qualification {index + 1}</strong>
                      {educations.length > 1 ? (
                        <button type="button" className="career__remove-btn" onClick={() => removeEducation(index)}>
                          Remove
                        </button>
                      ) : null}
                    </div>
                    <div className="career__form-grid">
                      <div className="career__field">
                        <FieldLabel required>Degree / Qualification</FieldLabel>
                        <input
                          className={`input ${errors[`edu-degree-${index}`] ? 'input--error' : ''}`}
                          value={edu.degree}
                          onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                        />
                        {errors[`edu-degree-${index}`] ? (
                          <span className="career__field-error">{errors[`edu-degree-${index}`]}</span>
                        ) : null}
                      </div>
                      <div className="career__field">
                        <FieldLabel required>Field of Study</FieldLabel>
                        <input
                          className={`input ${errors[`edu-field-${index}`] ? 'input--error' : ''}`}
                          value={edu.fieldOfStudy}
                          onChange={(e) => updateEducation(index, 'fieldOfStudy', e.target.value)}
                        />
                        {errors[`edu-field-${index}`] ? (
                          <span className="career__field-error">{errors[`edu-field-${index}`]}</span>
                        ) : null}
                      </div>
                      <div className="career__field">
                        <FieldLabel required>Institute / University</FieldLabel>
                        <input
                          className={`input ${errors[`edu-institute-${index}`] ? 'input--error' : ''}`}
                          value={edu.institute}
                          onChange={(e) => updateEducation(index, 'institute', e.target.value)}
                        />
                        {errors[`edu-institute-${index}`] ? (
                          <span className="career__field-error">{errors[`edu-institute-${index}`]}</span>
                        ) : null}
                      </div>
                      <div className="career__field">
                        <FieldLabel required>Board / University</FieldLabel>
                        <input
                          className={`input ${errors[`edu-board-${index}`] ? 'input--error' : ''}`}
                          value={edu.boardUniversity}
                          onChange={(e) => updateEducation(index, 'boardUniversity', e.target.value)}
                        />
                        {errors[`edu-board-${index}`] ? (
                          <span className="career__field-error">{errors[`edu-board-${index}`]}</span>
                        ) : null}
                      </div>
                      <div className="career__field">
                        <FieldLabel required>Passing Year</FieldLabel>
                        <input
                          className={`input ${errors[`edu-year-${index}`] ? 'input--error' : ''}`}
                          placeholder="e.g. 2020"
                          value={edu.passingYear}
                          onChange={(e) => updateEducation(index, 'passingYear', e.target.value)}
                        />
                        {errors[`edu-year-${index}`] ? (
                          <span className="career__field-error">{errors[`edu-year-${index}`]}</span>
                        ) : null}
                      </div>
                      <div className="career__field">
                        <FieldLabel>CGPA / Percentage / Division</FieldLabel>
                        <input
                          className="input"
                          placeholder="e.g. 3.5 / 75% / 1st Division"
                          value={edu.cgpaDivision}
                          onChange={(e) => updateEducation(index, 'cgpaDivision', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                {educations.length < MAX_ENTRIES ? (
                  <button type="button" className="career__add-btn" onClick={addEducation}>
                    + Add More
                  </button>
                ) : null}
              </FormSection>

              <FormSection title="4. Position Applied For">
                <div className="career__form-grid">
                  <div className="career__field">
                    <FieldLabel htmlFor="position" required>Position Applied For</FieldLabel>
                    <select
                      id="position"
                      className="input"
                      value={applyJobId}
                      onChange={(e) => handleJobChange(e.target.value)}
                    >
                      {JOBS.map((j) => (
                        <option key={j.id} value={j.id}>{j.title}</option>
                      ))}
                    </select>
                  </div>
                  <div className="career__field">
                    <FieldLabel htmlFor="department">Department</FieldLabel>
                    <select
                      id="department"
                      className="input"
                      value={positionPrefs.department}
                      onChange={(e) => updatePositionPrefs('department', e.target.value)}
                    >
                      {DEPARTMENTS.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                  <div className="career__field">
                    <FieldLabel htmlFor="preferredLocation">Preferred Job Location</FieldLabel>
                    <select
                      id="preferredLocation"
                      className="input"
                      value={positionPrefs.preferredLocation}
                      onChange={(e) => updatePositionPrefs('preferredLocation', e.target.value)}
                    >
                      <option value="">Optional</option>
                      {JOB_LOCATIONS.map((loc) => (
                        <option key={loc} value={loc}>{loc}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </FormSection>

              <FormSection title="5. Professional Experience">
                {experiences.map((exp, index) => (
                  <div key={index} className="career__repeat-block">
                    <div className="career__repeat-head">
                      <strong>Experience {index + 1}</strong>
                      {experiences.length > 1 ? (
                        <button type="button" className="career__remove-btn" onClick={() => removeExperience(index)}>
                          Remove
                        </button>
                      ) : null}
                    </div>
                    <div className="career__form-grid">
                      <div className="career__field">
                        <FieldLabel required>Organization Name</FieldLabel>
                        <input
                          className={`input ${errors[`exp-org-${index}`] ? 'input--error' : ''}`}
                          value={exp.organization}
                          onChange={(e) => updateExperience(index, 'organization', e.target.value)}
                        />
                        {errors[`exp-org-${index}`] ? (
                          <span className="career__field-error">{errors[`exp-org-${index}`]}</span>
                        ) : null}
                      </div>
                      <div className="career__field">
                        <FieldLabel required>Designation</FieldLabel>
                        <input
                          className={`input ${errors[`exp-designation-${index}`] ? 'input--error' : ''}`}
                          value={exp.designation}
                          onChange={(e) => updateExperience(index, 'designation', e.target.value)}
                        />
                        {errors[`exp-designation-${index}`] ? (
                          <span className="career__field-error">{errors[`exp-designation-${index}`]}</span>
                        ) : null}
                      </div>
                      <div className="career__field">
                        <FieldLabel required>Employment Duration — From</FieldLabel>
                        <input
                          type="month"
                          className={`input ${errors[`exp-start-${index}`] ? 'input--error' : ''}`}
                          value={exp.startDate}
                          onChange={(e) => updateExperience(index, 'startDate', e.target.value)}
                        />
                        {errors[`exp-start-${index}`] ? (
                          <span className="career__field-error">{errors[`exp-start-${index}`]}</span>
                        ) : null}
                      </div>
                      <div className="career__field">
                        <FieldLabel required={!exp.currentlyWorking}>Employment Duration — To</FieldLabel>
                        <input
                          type="month"
                          className={`input ${errors[`exp-end-${index}`] ? 'input--error' : ''}`}
                          value={exp.endDate}
                          disabled={exp.currentlyWorking}
                          onChange={(e) => updateExperience(index, 'endDate', e.target.value)}
                        />
                        {errors[`exp-end-${index}`] ? (
                          <span className="career__field-error">{errors[`exp-end-${index}`]}</span>
                        ) : null}
                      </div>
                      <div className="career__field career__field--full">
                        <label className="career__checkbox-label">
                          <input
                            type="checkbox"
                            checked={exp.currentlyWorking}
                            onChange={(e) => updateExperience(index, 'currentlyWorking', e.target.checked)}
                          />
                          Currently working here
                        </label>
                      </div>
                      <div className="career__field career__field--full">
                        <FieldLabel required>Key Responsibilities</FieldLabel>
                        <textarea
                          className={`input career__form-message ${errors[`exp-resp-${index}`] ? 'input--error' : ''}`}
                          rows={3}
                          value={exp.responsibilities}
                          onChange={(e) => updateExperience(index, 'responsibilities', e.target.value)}
                        />
                        {errors[`exp-resp-${index}`] ? (
                          <span className="career__field-error">{errors[`exp-resp-${index}`]}</span>
                        ) : null}
                      </div>
                      <div className="career__field career__field--full">
                        <FieldLabel>Reason for Leaving</FieldLabel>
                        <input
                          className="input"
                          value={exp.reasonForLeaving}
                          onChange={(e) => updateExperience(index, 'reasonForLeaving', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                {experiences.length < MAX_ENTRIES ? (
                  <button type="button" className="career__add-btn" onClick={addExperience}>
                    + Add More
                  </button>
                ) : null}
                {errors.experience ? <span className="career__field-error">{errors.experience}</span> : null}
              </FormSection>

              <FormSection title="6. Personal Statement">
                <div className="career__field">
                  <FieldLabel htmlFor="personalStatement" required>
                    Why are you suitable for this position? ({MIN_STATEMENT_WORDS}–{MAX_STATEMENT_WORDS} words)
                  </FieldLabel>
                  <textarea
                    id="personalStatement"
                    className={`input career__form-message ${errors.personalStatement ? 'input--error' : ''}`}
                    rows={6}
                    placeholder="Describe why you are suitable, your relevant skills and experience, and how you can contribute to the organization."
                    value={personalStatement}
                    onChange={(e) => setPersonalStatement(e.target.value)}
                  />
                  <div className="career__word-count">
                    <span
                      className={
                        statementWordCount < MIN_STATEMENT_WORDS || statementWordCount > MAX_STATEMENT_WORDS
                          ? 'career__word-count--warn'
                          : ''
                      }
                    >
                      {statementWordCount} / {MAX_STATEMENT_WORDS} words
                    </span>
                  </div>
                  {errors.personalStatement ? (
                    <span className="career__field-error">{errors.personalStatement}</span>
                  ) : null}
                </div>
              </FormSection>

              <FormSection title="7. Eligibility Confirmation">
                <div className="career__checkboxes">
                  <label className="career__checkbox-label">
                    <input
                      type="checkbox"
                      checked={confirmations.accurateInfo}
                      onChange={(e) => setConfirmations((prev) => ({ ...prev, accurateInfo: e.target.checked }))}
                    />
                    I confirm that all information provided is accurate and complete.
                  </label>
                  {errors.accurateInfo ? <span className="career__field-error">{errors.accurateInfo}</span> : null}

                  <label className="career__checkbox-label">
                    <input
                      type="checkbox"
                      checked={confirmations.meetsRequirements}
                      onChange={(e) => setConfirmations((prev) => ({ ...prev, meetsRequirements: e.target.checked }))}
                    />
                    I confirm that I meet the eligibility criteria for the advertised position.
                  </label>
                  {errors.meetsRequirements ? (
                    <span className="career__field-error">{errors.meetsRequirements}</span>
                  ) : null}

                  <label className="career__checkbox-label">
                    <input
                      type="checkbox"
                      checked={confirmations.agreesToPolicies}
                      onChange={(e) => setConfirmations((prev) => ({ ...prev, agreesToPolicies: e.target.checked }))}
                    />
                    I agree to the organization&apos;s recruitment policies.
                  </label>
                  {errors.agreesToPolicies ? (
                    <span className="career__field-error">{errors.agreesToPolicies}</span>
                  ) : null}
                </div>
              </FormSection>

              <FormSection title="8. Document Upload">
                <div className="career__form-grid">
                  <div className="career__field">
                    <FieldLabel htmlFor="cvFile" required>CV / Resume Upload</FieldLabel>
                    <input
                      id="cvFile"
                      className={`input career__form-file ${errors.cvFile ? 'input--error' : ''}`}
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => setCvFile(e.target.files?.[0] || null)}
                    />
                    <span className="career__file-hint">PDF, DOC, or DOCX — max {MAX_FILE_SIZE_MB} MB</span>
                    {errors.cvFile ? <span className="career__field-error">{errors.cvFile}</span> : null}
                  </div>
                  <div className="career__field">
                    <FieldLabel htmlFor="supportingFiles">Supporting Documents Upload</FieldLabel>
                    <input
                      id="supportingFiles"
                      className="input career__form-file"
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      multiple
                      onChange={(e) => setSupportingFiles(Array.from(e.target.files || []))}
                    />
                    <span className="career__file-hint">
                      Educational degrees, academic transcripts, professional certificates, experience certificates,
                      and other relevant documents
                    </span>
                    {supportingFiles.length > 0 ? (
                      <span className="career__file-count">{supportingFiles.length} file(s) selected</span>
                    ) : null}
                  </div>
                </div>
              </FormSection>

              {false && eligibilityResult ? (
                <div
                  id="eligibility-screening"
                  className={`career__form-section career__screening career__screening--reveal ${isEligible ? 'career__screening--eligible' : 'career__screening--ineligible'}`}
                >
                  <h4 className="career__form-section-title">9. Automated Eligibility Screening</h4>

                  {isEligible ? (
                    <div className="career__eligibility career__eligibility--eligible">
                      <h3>✅ You are Eligible for this Job</h3>
                      <p>
                        You meet the minimum eligibility criteria for <strong>{selectedJob.title}</strong>. You may
                        now submit your application.
                      </p>
                      {eligibilityResult.experienceYears != null ? (
                        <p className="career__screening-meta">
                          Relevant experience: ~{eligibilityResult.experienceYears.toFixed(1)} years
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <div className="career__eligibility career__eligibility--ineligible">
                      <h3>❌ You are Not Eligible for this Job</h3>
                      <p>You do not meet the minimum requirements for this position:</p>
                      <ul className="career__bullets">
                        {eligibilityResult.reasons.map((reason) => (
                          <li key={reason}>{reason}</li>
                        ))}
                      </ul>
                      <p className="section-text">
                        Please update your education or experience details above, or apply for another opening.
                      </p>
                    </div>
                  )}
                </div>
              ) : null}

              <div className="career__actions">
                <button
                  type="button"
                  className="btn-outline"
                  onClick={() => setPage('career-details', { params: { jobId: applyJobId } })}
                >
                  Back to Details
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ marginLeft: 10 }}
                  disabled={formComplete && !isEligible}
                  title={formComplete && !isEligible ? 'You are not eligible for this job' : undefined}
                >
                  Submit Application
                </button>
              </div>
              {errors.eligibility ? <span className="career__field-error">{errors.eligibility}</span> : null}

              {eligibilityVisible && eligibilityResult ? (
                <div
                  id="eligibility-screening"
                  className={`career__form-section career__screening career__screening--reveal ${isEligible ? 'career__screening--eligible' : 'career__screening--ineligible'}`}
                >
                  <h4 className="career__form-section-title">9. Automated Eligibility Screening</h4>

                  {isEligible ? (
                    <div className="career__eligibility career__eligibility--eligible">
                      <h3>You are Eligible for this Job</h3>
                      <p>
                        You meet the minimum eligibility criteria for <strong>{selectedJob.title}</strong>. You may
                        now submit your application.
                      </p>
                      {eligibilityResult.experienceYears != null ? (
                        <p className="career__screening-meta">
                          Relevant experience: ~{eligibilityResult.experienceYears.toFixed(1)} years
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <div className="career__eligibility career__eligibility--ineligible">
                      <h3>You are Not Eligible for this Job</h3>
                      <p>You do not meet the minimum requirements for this position:</p>
                      <ul className="career__bullets">
                        {eligibilityResult.reasons.map((reason) => (
                          <li key={reason}>{reason}</li>
                        ))}
                      </ul>
                      <p className="section-text">
                        Please update your education or experience details above, or apply for another opening.
                      </p>
                    </div>
                  )}
                </div>
              ) : null}
            </form>

            <div className="career__contact">
              <div className="career__contact-item">
                <strong>Email Us</strong>
                <span>ccihisb@gmail.com</span>
              </div>
              <div className="career__contact-item">
                <strong>WhatsApp</strong>
                <span>0333-1117290</span>
              </div>
              <p className="section-text career__thanks">
                 Only contact with shortlisted candidates for interview
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
